import React, { useState, useEffect, useRef } from "react";
import { NavLink, Link } from "react-router-dom";
import { FaEnvelope, FaPhone } from "react-icons/fa";
import Services14 from "../images/flogo.png";

/* ── Constants ─────────────────────────────────────── */
const CONTACT = {
  email:  "info@skywisetechnologies.in",
  phone:  "+91 7318731222",
  tel:    "+91 7318731222",
  gmail:  "https://mail.google.com/mail/?view=cm&fs=1&to=info@skywisetechnologies.in",
};

const NAV_LINKS = [
  { label: "Home",       to: "/"           },
  { label: "Technology", to: "/technology" },
  { label: "About",      to: "/about"      },
  { label: "Service",    to: "/service"    },
  { label: "Contact",    to: "/contact"    },
];

/* ── Navbar ─────────────────────────────────────────── */
function Navbar() {
  const [menuOpen,    setMenuOpen]    = useState(false);
  const [scrolled,    setScrolled]    = useState(false);
  const [dropdownOpen, setDropdownOpen] = useState(false);
  const menuRef   = useRef(null);
  const dropRef   = useRef(null);

  /* scroll effect */
  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 20);
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  /* close menu / dropdown on outside click */
  useEffect(() => {
    const handler = (e) => {
      if (menuRef.current && !menuRef.current.contains(e.target))
        setMenuOpen(false);
      if (dropRef.current && !dropRef.current.contains(e.target))
        setDropdownOpen(false);
    };
    document.addEventListener("mousedown", handler);
    return () => document.removeEventListener("mousedown", handler);
  }, []);

  /* close mobile menu on route change */
  const handleNavClick = () => setMenuOpen(false);

  return (
    <>
      {/* ── Scoped styles ── */}
      <style>{`
        @import url('https://fonts.googleapis.com/css2?family=Syne:wght@600;700;800&family=DM+Sans:wght@400;500;600&display=swap');

        :root {
          --nav-bg-idle:       rgba(10, 20, 35, 0.55);
          --nav-bg-scrolled:   rgba(8, 16, 30, 0.97);
          --nav-border:        rgba(255,255,255,0.08);
          --accent:            #00c2ff;
          --accent-glow:       rgba(0,194,255,0.25);
          --text-dim:          rgba(255,255,255,0.65);
          --text-bright:       #ffffff;
          --mobile-bg:         #080f1c;
          --font-display:      'Syne', sans-serif;
          --font-body:         'DM Sans', sans-serif;
          --nav-h:             68px;
        }

        /* ── Bar ── */
        .snav {
          position: fixed;
          top: 0; left: 0; right: 0;
          z-index: 1000;
          height: var(--nav-h);
          display: flex;
          align-items: center;
          background: var(--nav-bg-idle);
          backdrop-filter: blur(18px) saturate(160%);
          -webkit-backdrop-filter: blur(18px) saturate(160%);
          border-bottom: 1px solid var(--nav-border);
          transition: background 0.35s ease, box-shadow 0.35s ease;
          font-family: var(--font-body);
        }
        .snav.scrolled {
          background: var(--nav-bg-scrolled);
          box-shadow: 0 4px 32px rgba(0,0,0,0.45);
        }

        /* ── Inner container ── */
        .snav-inner {
          width: 100%;
          max-width: 1280px;
          margin: 0 auto;
          padding: 0 1.5rem;
          display: flex;
          align-items: center;
          justify-content: space-between;
          gap: 1rem;
        }

        /* ── Logo ── */
        .snav-logo {
          display: flex;
          align-items: center;
          gap: 0.5rem;
          text-decoration: none;
          flex-shrink: 0;
        }
        .snav-logo img {
          height: 40px;
          width: auto;
          object-fit: contain;
          transition: opacity 0.2s;
        }
        .snav-logo:hover img { opacity: 0.85; }

        /* ── Desktop links ── */
        .snav-links {
          display: flex;
          align-items: center;
          gap: 0.25rem;
          list-style: none;
          margin: 0; padding: 0;
        }
        .snav-link {
          position: relative;
          padding: 0.45rem 0.85rem;
          font-size: 0.9rem;
          font-weight: 500;
          color: var(--text-dim);
          text-decoration: none;
          letter-spacing: 0.02em;
          border-radius: 6px;
          transition: color 0.2s ease, background 0.2s ease;
          white-space: nowrap;
        }
        .snav-link::after {
          content: '';
          position: absolute;
          bottom: 4px; left: 50%;
          transform: translateX(-50%) scaleX(0);
          width: 18px; height: 2px;
          background: var(--accent);
          border-radius: 2px;
          transition: transform 0.25s ease;
        }
        .snav-link:hover,
        .snav-link.active {
          color: var(--text-bright);
          background: rgba(255,255,255,0.06);
        }
        .snav-link:hover::after,
        .snav-link.active::after {
          transform: translateX(-50%) scaleX(1);
        }
        .snav-link.active {
          color: var(--accent);
          background: rgba(0,194,255,0.08);
        }

        /* ── CTA group ── */
        .snav-cta {
          display: flex;
          align-items: center;
          gap: 0.6rem;
          flex-shrink: 0;
        }
        .snav-btn-ghost {
          display: flex;
          align-items: center;
          gap: 0.4rem;
          padding: 0.42rem 1rem;
          border: 1px solid rgba(255,255,255,0.2);
          border-radius: 6px;
          color: var(--text-dim);
          font-family: var(--font-body);
          font-size: 0.82rem;
          font-weight: 500;
          text-decoration: none;
          background: rgba(255,255,255,0.04);
          transition: border-color 0.2s, color 0.2s, background 0.2s;
          white-space: nowrap;
        }
        .snav-btn-ghost:hover {
          border-color: var(--accent);
          color: var(--accent);
          background: rgba(0,194,255,0.07);
        }
        .snav-btn-accent {
          display: flex;
          align-items: center;
          gap: 0.4rem;
          padding: 0.42rem 1.1rem;
          background: var(--accent);
          border: 1px solid var(--accent);
          border-radius: 6px;
          color: #060e1a;
          font-family: var(--font-body);
          font-size: 0.82rem;
          font-weight: 700;
          text-decoration: none;
          letter-spacing: 0.01em;
          transition: box-shadow 0.25s ease, opacity 0.2s;
          white-space: nowrap;
        }
        .snav-btn-accent:hover {
          opacity: 0.88;
          box-shadow: 0 0 18px var(--accent-glow);
        }
        .snav-btn-icon {
          font-size: 0.8rem;
          opacity: 0.85;
          flex-shrink: 0;
        }

        /* ── Hamburger ── */
        .snav-hamburger {
          display: none;
          flex-direction: column;
          justify-content: center;
          gap: 5px;
          width: 36px; height: 36px;
          padding: 6px;
          background: rgba(255,255,255,0.06);
          border: 1px solid rgba(255,255,255,0.1);
          border-radius: 6px;
          cursor: pointer;
          transition: background 0.2s;
          flex-shrink: 0;
        }
        .snav-hamburger:hover { background: rgba(255,255,255,0.12); }
        .snav-hamburger span {
          display: block;
          width: 100%;
          height: 2px;
          background: var(--text-bright);
          border-radius: 2px;
          transition: transform 0.3s ease, opacity 0.3s ease;
          transform-origin: center;
        }
        .snav-hamburger.open span:nth-child(1) { transform: translateY(7px) rotate(45deg); }
        .snav-hamburger.open span:nth-child(2) { opacity: 0; transform: scaleX(0); }
        .snav-hamburger.open span:nth-child(3) { transform: translateY(-7px) rotate(-45deg); }

        /* ── Mobile drawer ── */
        .snav-drawer {
          display: none;
          position: fixed;
          top: var(--nav-h);
          left: 0; right: 0;
          background: var(--mobile-bg);
          border-top: 1px solid var(--nav-border);
          border-bottom: 1px solid var(--nav-border);
          padding: 1.25rem 1.5rem 1.75rem;
          z-index: 999;
          transform: translateY(-8px);
          opacity: 0;
          pointer-events: none;
          transition: transform 0.3s ease, opacity 0.3s ease;
        }
        .snav-drawer.open {
          transform: translateY(0);
          opacity: 1;
          pointer-events: auto;
        }
        .snav-drawer-links {
          list-style: none;
          padding: 0; margin: 0 0 1.25rem;
          display: flex;
          flex-direction: column;
          gap: 0.15rem;
        }
        .snav-drawer-link {
          display: block;
          padding: 0.7rem 0.9rem;
          font-size: 1rem;
          font-weight: 500;
          color: var(--text-dim);
          text-decoration: none;
          border-radius: 8px;
          transition: background 0.2s, color 0.2s;
          font-family: var(--font-body);
        }
        .snav-drawer-link:hover,
        .snav-drawer-link.active {
          color: var(--text-bright);
          background: rgba(255,255,255,0.07);
        }
        .snav-drawer-link.active {
          color: var(--accent);
          background: rgba(0,194,255,0.08);
        }
        .snav-drawer-divider {
          height: 1px;
          background: var(--nav-border);
          margin: 0.75rem 0 1rem;
        }
        .snav-drawer-cta {
          display: flex;
          flex-direction: column;
          gap: 0.6rem;
        }
        .snav-drawer-cta a {
          text-align: center;
          border-radius: 8px;
          font-size: 0.9rem;
          font-weight: 600;
          padding: 0.65rem 1rem;
          text-decoration: none;
          transition: opacity 0.2s, box-shadow 0.2s;
          font-family: var(--font-body);
        }
        .snav-drawer-cta .ghost {
          color: var(--text-dim);
          border: 1px solid rgba(255,255,255,0.18);
          background: rgba(255,255,255,0.04);
        }
        .snav-drawer-cta .ghost:hover { color: var(--accent); border-color: var(--accent); }
        .snav-drawer-cta .primary {
          background: var(--accent);
          color: #060e1a;
        }
        .snav-drawer-cta .primary:hover {
          opacity: 0.88;
          box-shadow: 0 0 18px var(--accent-glow);
        }

        /* ── Responsive ── */
        @media (max-width: 1024px) {
          .snav-btn-ghost { display: none; }
        }
        @media (max-width: 768px) {
          .snav-links,
          .snav-cta     { display: none; }
          .snav-hamburger { display: flex; }
          .snav-drawer    { display: block; }
        }
        @media (max-width: 400px) {
          .snav-inner { padding: 0 1rem; }
          .snav-logo img { height: 34px; }
        }
      `}</style>

      {/* ── Navbar bar ── */}
      <nav className={`snav ${scrolled ? "scrolled" : ""}`} role="navigation" aria-label="Main navigation">
        <div className="snav-inner">

          {/* Logo */}
          <Link to="/" className="snav-logo" aria-label="SkyWise Technologies — Home">
            <img src={Services14} alt="SkyWise Technologies" />
          </Link>

          {/* Desktop nav links */}
          <ul className="snav-links" role="menubar">
            {NAV_LINKS.map(({ label, to }) => (
              <li key={to} role="none">
                <NavLink
                  to={to}
                  end={to === "/"}
                  className={({ isActive }) =>
                    `snav-link${isActive ? " active" : ""}`
                  }
                  role="menuitem"
                >
                  {label}
                </NavLink>
              </li>
            ))}
          </ul>

          {/* Desktop CTA */}
          <div className="snav-cta">
            <a
              href={CONTACT.gmail}
              target="_blank"
              rel="noopener noreferrer"
              className="snav-btn-ghost"
              aria-label={`Send us an email at ${CONTACT.email}`}
            >
              <FaEnvelope className="snav-btn-icon" />
              {CONTACT.email}
            </a>
            <a
              href={`tel:${CONTACT.tel}`}
              className="snav-btn-accent"
              aria-label={`Call us at ${CONTACT.phone}`}
            >
              <FaPhone className="snav-btn-icon" />
              {CONTACT.phone}
            </a>
          </div>

          {/* Hamburger (mobile) */}
          <button
            className={`snav-hamburger ${menuOpen ? "open" : ""}`}
            onClick={() => setMenuOpen((v) => !v)}
            aria-expanded={menuOpen}
            aria-controls="snav-mobile-menu"
            aria-label={menuOpen ? "Close menu" : "Open menu"}
          >
            <span />
            <span />
            <span />
          </button>
        </div>
      </nav>

      {/* ── Mobile drawer ── */}
      <div
        id="snav-mobile-menu"
        ref={menuRef}
        className={`snav-drawer ${menuOpen ? "open" : ""}`}
        role="dialog"
        aria-modal="false"
        aria-label="Mobile navigation"
      >
        <ul className="snav-drawer-links">
          {NAV_LINKS.map(({ label, to }) => (
            <li key={to}>
              <NavLink
                to={to}
                end={to === "/"}
                className={({ isActive }) =>
                  `snav-drawer-link${isActive ? " active" : ""}`
                }
                onClick={handleNavClick}
              >
                {label}
              </NavLink>
            </li>
          ))}
        </ul>

        <div className="snav-drawer-divider" />

        <div className="snav-drawer-cta">
          <a
            href={CONTACT.gmail}
            target="_blank"
            rel="noopener noreferrer"
            className="ghost"
            onClick={handleNavClick}
            aria-label={`Send us an email at ${CONTACT.email}`}
          >
            <FaEnvelope style={{ marginRight: "0.45rem", fontSize: "0.85rem" }} />
            {CONTACT.email}
          </a>
          <a
            href={`tel:${CONTACT.tel}`}
            className="primary"
            onClick={handleNavClick}
            aria-label={`Call us at ${CONTACT.phone}`}
          >
            <FaPhone style={{ marginRight: "0.45rem", fontSize: "0.85rem" }} />
            {CONTACT.phone}
          </a>
        </div>
      </div>
    </>
  );
}

export default Navbar;